import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { CircleNotch, CheckCircle, ShieldCheck, IdentificationCard } from "@phosphor-icons/react";

export function SampleBadge({ testid = "mono-sample-badge" }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#FBF1DC] text-[#B45309] text-[11px] font-semibold px-2.5 py-1" data-testid={testid}>
      Sample data: Mono not connected yet
    </span>
  );
}

const errMsg = (e) => formatErr(e?.response?.data?.detail) || e.message;

/** BVN (Mono iGree one-time code) then NIN check. Calls onVerified({ bvn, nin, identity, matchScore, sample }). */
export default function MonoIdentityVerify({ onVerified, onReset, prior }) {
  const [usePrior, setUsePrior] = useState(!!prior);
  const [bvn, setBvn] = useState("");
  const [nin, setNin] = useState("");
  const [session, setSession] = useState(null);
  const [method, setMethod] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sampleOtp, setSampleOtp] = useState("");
  const [otp, setOtp] = useState("");
  const [bvnIdent, setBvnIdent] = useState(null);
  const [ninDone, setNinDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };

  const sendCode = (sid, m) => run(async () => {
    const { data } = await api.post("/mono/bvn/verify", { session_id: sid, method: m });
    setMethod(m); setOtpSent(true);
    if (data.sample_otp) setSampleOtp(data.sample_otp);
    toast.success("Code sent to the phone registered on your BVN");
  });

  const start = () => run(async () => {
    const { data } = await api.post("/mono/bvn/initiate", { bvn });
    setSession(data); setOtpSent(false); setOtp("");
    if (data.methods.length === 1) {
      const { data: v } = await api.post("/mono/bvn/verify", { session_id: data.session_id, method: data.methods[0].method });
      setMethod(data.methods[0].method); setOtpSent(true);
      if (v.sample_otp) setSampleOtp(v.sample_otp);
      toast.success("Code sent to the phone registered on your BVN");
    }
  });

  const confirmOtp = () => run(async () => {
    const { data } = await api.post("/mono/bvn/details", { session_id: session.session_id, otp });
    setBvnIdent(data);
    toast.success("BVN verified");
  });

  const checkNin = () => run(async () => {
    const { data } = await api.post("/mono/nin", { nin });
    setNinDone(data);
    onVerified({ bvn, nin, identity: bvnIdent.identity, matchScore: data.match_score, sample: data.sample });
  });

  const reset = () => {
    setBvn(""); setNin(""); setSession(null); setMethod(""); setOtpSent(false);
    setSampleOtp(""); setOtp(""); setBvnIdent(null); setNinDone(null);
    onReset?.();
  };

  const sample = session?.sample || bvnIdent?.sample;

  if (usePrior) return (
    <div className="p-4 rounded-2xl bg-[#E2F3EB]/40 border border-[#2D7A5D]/20 flex items-center justify-between gap-3 flex-wrap" data-testid="mono-identity-prior">
      <div className="text-sm text-[#2D7A5D] flex items-center gap-2">
        <CheckCircle size={16} weight="fill" /> Identity already verified · BVN ****{prior.bvn_last4} · NIN ****{prior.nin_last4}
      </div>
      <button type="button" onClick={() => { setUsePrior(false); onReset?.(); }} className="text-xs underline text-[#0A3622]" data-testid="mono-identity-change">Use a different BVN/NIN</button>
    </div>
  );

  return (
    <div className="p-4 rounded-2xl bg-[#F0EFEA]/60 border border-[#E5E3DB] space-y-4" data-testid="mono-identity-verify">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-semibold text-[#0A3622] flex items-center gap-2"><IdentificationCard size={16} weight="fill" /> Identity check by Mono</div>
        {sample && <SampleBadge />}
      </div>

      {/* Step 1 — BVN */}
      {!bvnIdent ? (
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">BVN (11 digits)</span>
            <input className="input mt-1 font-mono" maxLength={11} value={bvn} disabled={otpSent}
              onChange={(e) => setBvn(e.target.value.replace(/\D/g, ""))} placeholder="22XXXXXXXXX" data-testid="mono-bvn-input" />
          </label>
          {!otpSent ? (
            <div className="flex items-end">
              <button type="button" onClick={start} disabled={busy || bvn.length !== 11} className="btn-primary !py-2 w-full justify-center disabled:opacity-50" data-testid="mono-bvn-start">
                {busy ? <CircleNotch className="animate-spin" size={16} /> : "Send code to my BVN phone"}
              </button>
            </div>
          ) : (
            <label className="block">
              <span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">Code from SMS</span>
              <div className="flex gap-2 mt-1">
                <input className="input font-mono tracking-widest" maxLength={8} value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="123456" data-testid="mono-bvn-otp-input" />
                <button type="button" onClick={confirmOtp} disabled={busy || otp.length < 4} className="btn-primary !py-2 shrink-0 disabled:opacity-50" data-testid="mono-bvn-otp-submit">
                  {busy ? <CircleNotch className="animate-spin" size={16} /> : "Confirm"}
                </button>
              </div>
              {sampleOtp && <span className="block text-xs text-[#B45309] mt-1" data-testid="mono-sample-otp">Sample mode: use {sampleOtp}</span>}
              <button type="button" onClick={start} disabled={busy} className="text-xs text-[#0A3622] underline mt-1" data-testid="mono-bvn-restart">Start again</button>
            </label>
          )}
          {session && session.methods.length > 1 && !method && (
            <div className="sm:col-span-2 space-y-2" data-testid="mono-bvn-methods">
              <div className="text-xs text-[#4F5D4F]">Where should we send your code?</div>
              {session.methods.map((m) => (
                <button key={m.method} type="button" disabled={busy} onClick={() => sendCode(session.session_id, m.method)}
                  className="btn-outline !py-2 w-full justify-start text-sm" data-testid={`mono-bvn-method-${m.method}`}>
                  {m.hint || m.method}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-start gap-2 text-sm text-[#2D7A5D]" data-testid="mono-bvn-verified">
          <CheckCircle size={18} weight="fill" className="mt-0.5 shrink-0" />
          <span>BVN verified for <strong className="text-[#0A3622]">{bvnIdent.identity.full_name}</strong> · born {bvnIdent.identity.dob}</span>
          <button type="button" onClick={reset} className="ml-auto text-xs underline text-[#0A3622]" data-testid="mono-identity-reset">Use a different BVN</button>
        </div>
      )}

      {/* Step 2 — NIN */}
      {bvnIdent && !ninDone && (
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">NIN (11 digits)</span>
            <input className="input mt-1 font-mono" maxLength={11} value={nin}
              onChange={(e) => setNin(e.target.value.replace(/\D/g, ""))} placeholder="12345678901" data-testid="mono-nin-input" />
          </label>
          <div className="flex items-end">
            <button type="button" onClick={checkNin} disabled={busy || nin.length !== 11} className="btn-primary !py-2 w-full justify-center disabled:opacity-50" data-testid="mono-nin-submit">
              {busy ? <CircleNotch className="animate-spin" size={16} /> : <>Check NIN <ShieldCheck size={16} weight="fill" /></>}
            </button>
          </div>
        </div>
      )}
      {ninDone && (
        <div className={`flex items-start gap-2 text-sm ${ninDone.match_score >= 90 ? "text-[#2D7A5D]" : "text-[#B45309]"}`} data-testid="mono-nin-result">
          <CheckCircle size={18} weight="fill" className="mt-0.5 shrink-0" />
          <span>NIN checked · BVN and NIN match {ninDone.match_score ?? 0}%</span>
        </div>
      )}
    </div>
  );
}
